import React, { useEffect, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { qty, type ProcessRecord, type WorkOrderRecord, type WorkOrderStageRecord } from "@/types/production";
import type { SupplierRecord } from "@/types/inventory";

const field = "w-full h-10 rounded-lg border border-input bg-background px-3 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring";
const today = () => new Date().toISOString().slice(0, 10);

interface Base {
  workOrder: WorkOrderRecord;
  onClose: () => void;
  onSaved: () => void;
}

/** Wraps the dialog chrome, the saving flag and error toast shared by all the stage dialogs */
function useAction(onSaved: () => void, onClose: () => void) {
  const { success, error } = useToast();
  const [saving, setSaving] = useState(false);
  const run = async (fn: () => Promise<unknown>, done: string) => {
    setSaving(true);
    try {
      await fn();
      success(done, "");
      onSaved();
      onClose();
    } catch (err: any) {
      error("Could not save", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };
  return { saving, run, error };
}

/** Finish a stage: what came out, and what was lost on the way */
export function CompleteStageDialog({ workOrder, stage, onClose, onSaved }: Base & { stage: WorkOrderStageRecord | null }) {
  const { saving, run, error } = useAction(onSaved, onClose);
  const [f, setF] = useState({ output_qty: "", input_qty: "", wastage_qty: "", actual_end: today(), remarks: "" });
  useEffect(() => {
    if (stage) setF({ output_qty: "", input_qty: stage.input_qty != null ? String(Number(stage.input_qty)) : "", wastage_qty: "", actual_end: today(), remarks: stage.remarks || "" });
  }, [stage?.id]);
  const input = Number(f.input_qty) || 0;
  const output = Number(f.output_qty) || 0;
  const auto = Math.max(0, Math.round((input - output) * 1000) / 1000);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!stage) return;
    if (!(output > 0)) return error("Validation Error", "Enter the quantity that came out");
    void run(
      () =>
        api.post(`/api/WorkOrder/StageComplete/${workOrder.id}/${stage.id}`, {
          output_qty: output,
          input_qty: f.input_qty === "" ? undefined : Number(f.input_qty),
          wastage_qty: f.wastage_qty === "" ? undefined : Number(f.wastage_qty),
          actual_end: f.actual_end || undefined,
          remarks: f.remarks.trim() || undefined,
        }),
      `${stage.process_name} completed`,
    );
  };
  return (
    <Dialog open={!!stage} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Complete {stage?.process_name}</DialogTitle>
          <DialogDescription>{workOrder.wo_number} · {workOrder.product_name}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 py-2 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Went in ({workOrder.unit})</Label>
              <Input inputMode="decimal" value={f.input_qty} onChange={(e) => setF({ ...f, input_qty: e.target.value })} className="h-10" />
            </div>
            <div className="space-y-1.5">
              <Label>Came out ({workOrder.unit}) *</Label>
              <Input inputMode="decimal" autoFocus value={f.output_qty} onChange={(e) => setF({ ...f, output_qty: e.target.value })} className="h-10" />
            </div>
            <div className="space-y-1.5">
              <Label>Lost (wastage)</Label>
              <Input inputMode="decimal" value={f.wastage_qty} onChange={(e) => setF({ ...f, wastage_qty: e.target.value })} placeholder={`${auto} (in − out)`} className="h-10" />
            </div>
            <div className="space-y-1.5">
              <Label>Finished on</Label>
              <Input type="date" value={f.actual_end} onChange={(e) => setF({ ...f, actual_end: e.target.value })} className="h-10" />
            </div>
          </div>
          {input > 0 && output > 0 && <p className="text-xs text-muted-foreground">Yield of this stage: {Math.round((output / input) * 1000) / 10}%</p>}
          <div className="space-y-1.5">
            <Label>Remarks</Label>
            <Textarea rows={2} value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving} className="gap-1.5">
              {saving && <Loader2 className="size-4 animate-spin" />}
              Complete stage
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Start a stage; for job work the job worker has to be chosen first (in Plan) */
export function StartStageDialog({ workOrder, stage, onClose, onSaved }: Base & { stage: WorkOrderStageRecord | null }) {
  const { saving, run } = useAction(onSaved, onClose);
  const [date, setDate] = useState(today());
  const [input, setInput] = useState("");
  useEffect(() => {
    setDate(today());
    setInput("");
  }, [stage?.id]);
  return (
    <Dialog open={!!stage} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Start {stage?.process_name}</DialogTitle>
          <DialogDescription>{stage?.execution === "JOB_WORK" ? `At ${stage.supplier?.name || "a job worker"}` : "In-house"} · planned {stage?.planned_start || "-"} to {stage?.planned_end || "-"}</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (stage) void run(() => api.post(`/api/WorkOrder/StageStart/${workOrder.id}/${stage.id}`, { actual_start: date || undefined, input_qty: input === "" ? undefined : Number(input) }), `${stage.process_name} started`);
          }}
          className="space-y-4 py-2 text-sm"
        >
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Started on</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10" />
            </div>
            <div className="space-y-1.5">
              <Label>Quantity going in ({workOrder.unit})</Label>
              <Input inputMode="decimal" placeholder="From the stage before" value={input} onChange={(e) => setInput(e.target.value)} className="h-10" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving} className="gap-1.5">
              {saving && <Loader2 className="size-4 animate-spin" />}
              Start stage
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** One text box for a reason: skipping a stage, cancelling the work order */
export function ReasonDialog({ open, title, description, confirmLabel, destructive, onClose, onConfirm }: { open: boolean; title: string; description?: string; confirmLabel: string; destructive?: boolean; onClose: () => void; onConfirm: (reason: string) => Promise<unknown> }) {
  const { error } = useToast();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => setReason(""), [open]);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return error("Validation Error", "Give a reason");
    setBusy(true);
    try {
      await onConfirm(reason.trim());
      onClose();
    } catch (err: any) {
      error("Could not save", err.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 py-2 text-sm">
          <Textarea rows={3} autoFocus value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Back</Button>
            <Button type="submit" variant={destructive ? "destructive" : "default"} disabled={busy} className="gap-1.5">
              {busy && <Loader2 className="size-4 animate-spin" />}
              {confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Who does a stage and when it is planned */
export function PlanStageDialog({ workOrder, stage, onClose, onSaved }: Base & { stage: WorkOrderStageRecord | null }) {
  const { saving, run } = useAction(onSaved, onClose);
  const [suppliers, setSuppliers] = useState<SupplierRecord[]>([]);
  const [f, setF] = useState({ execution: "IN_HOUSE", supplier_id: "", planned_start: "", planned_end: "", remarks: "" });
  useEffect(() => {
    if (!stage) return;
    setF({ execution: stage.execution, supplier_id: stage.supplier_id || "", planned_start: stage.planned_start || "", planned_end: stage.planned_end || "", remarks: stage.remarks || "" });
    api.get("/api/Supplier/List?page_size=100").then((r: any) => setSuppliers(unwrapArray<SupplierRecord>(r?.rows ? r.rows : r))).catch(() => setSuppliers([]));
  }, [stage?.id]);
  return (
    <Dialog open={!!stage} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Plan {stage?.process_name}</DialogTitle>
          <DialogDescription>{workOrder.wo_number}</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (stage)
              void run(
                () => api.put(`/api/WorkOrder/StageUpdate/${workOrder.id}/${stage.id}`, { execution: f.execution, supplier_id: f.execution === "JOB_WORK" ? f.supplier_id || null : null, planned_start: f.planned_start || null, planned_end: f.planned_end || null, remarks: f.remarks }),
                "Stage plan saved",
              );
          }}
          className="space-y-4 py-2 text-sm"
        >
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Done</Label>
              <select className={field} value={f.execution} onChange={(e) => setF({ ...f, execution: e.target.value })}>
                <option value="IN_HOUSE">In-house</option>
                <option value="JOB_WORK">By a job worker</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Job worker</Label>
              <select className={field} value={f.supplier_id} onChange={(e) => setF({ ...f, supplier_id: e.target.value })} disabled={f.execution !== "JOB_WORK"}>
                <option value="">-- Choose --</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Planned start</Label>
              <Input type="date" value={f.planned_start} onChange={(e) => setF({ ...f, planned_start: e.target.value })} className="h-10" />
            </div>
            <div className="space-y-1.5">
              <Label>Planned end</Label>
              <Input type="date" value={f.planned_end} onChange={(e) => setF({ ...f, planned_end: e.target.value })} className="h-10" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Remarks</Label>
            <Textarea rows={2} value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving} className="gap-1.5">
              {saving && <Loader2 className="size-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Add a stage at the end, or before one that has not started */
export function AddStageDialog({ workOrder, open, onClose, onSaved }: Base & { open: boolean }) {
  const { saving, run, error } = useAction(onSaved, onClose);
  const [processes, setProcesses] = useState<ProcessRecord[]>([]);
  const [code, setCode] = useState("");
  const [before, setBefore] = useState("");
  const pending = (workOrder.stages || []).filter((s) => s.stage_status === "PENDING");
  useEffect(() => {
    if (!open) return;
    setCode("");
    setBefore("");
    api.get("/api/ProcessMaster/List").then((r) => setProcesses(unwrapArray<ProcessRecord>(r))).catch(() => setProcesses([]));
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add a stage</DialogTitle>
          <DialogDescription>{workOrder.wo_number} · {qty(workOrder.planned_qty, workOrder.unit)}</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!code) return error("Validation Error", "Choose the process");
            void run(() => api.post(`/api/WorkOrder/StageAdd/${workOrder.id}`, { process_code: code, before_stage_id: before || undefined }), "Stage added");
          }}
          className="space-y-4 py-2 text-sm"
        >
          <div className="space-y-1.5">
            <Label>Process *</Label>
            <select className={field} value={code} onChange={(e) => setCode(e.target.value)}>
              <option value="">-- Choose --</option>
              {processes.map((p) => (
                <option key={p.code} value={p.code}>{p.name}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Where</Label>
            <select className={field} value={before} onChange={(e) => setBefore(e.target.value)}>
              <option value="">At the end</option>
              {pending.map((s) => (
                <option key={s.id} value={s.id}>Before {s.process_name}</option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving} className="gap-1.5">
              {saving && <Loader2 className="size-4 animate-spin" />}
              Add stage
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
